import * as THREE from 'three';
import { CAMERA } from '../core/SimConfig.js';
import { clamp, DEG2RAD, RAD2DEG } from '../utils.js';

/**
 * Fixed "action camera" mounts baked into the A-10 model (CameraMode.ACTION).
 *
 * Every node under the aircraft whose name starts with `CAMERA.action.prefix`
 * is one shot. The camera rides that node's world transform rigidly — no
 * smoothing, no look input — so a mount costs two matrix reads a frame and the
 * player keeps flying the jet while watching from it. Deliberately rock steady:
 * these are hard-mounted lenses, not the pilot's head, which is what makes them
 * read as camera footage rather than as another view mode.
 *
 * Blender's glTF exporter writes them as real cameras (Include → Cameras), so
 * each shot carries its authored lens; a node that arrives as a plain empty
 * because that switch was off still works, at `CAMERA.action.fallbackFov`.
 *
 * The node *name* is also the shot's dressing list: `_cockpit` draws the HD
 * interior in place of the SD shell, `_pilot` draws the seated pilot. Naming a
 * mount in Blender is therefore the whole wiring — no code knows how many
 * there are or where they point.
 */
export default class ActionCamera {
  constructor({ model }) {
    // Output — CameraManager reads these
    this.position = new THREE.Vector3();
    this.quaternion = new THREE.Quaternion();

    const cfg = CAMERA.action;
    const nodes = [];
    model.traverse((o) => {
      if (o.name && o.name.toLowerCase().startsWith(cfg.prefix)) nodes.push(o);
    });
    // Name order is shot order, so the cycle follows the cam_01…cam_NN numbering
    // the artist authored rather than glTF node order.
    nodes.sort((a, b) => a.name.localeCompare(b.name));

    this.shots = nodes.map((node) => {
      const name = node.name.toLowerCase();
      const interior = name.includes(cfg.tagCockpit);
      return {
        node,
        name: node.name,
        // Vertical FOV and the aspect it was authored at (glTF yfov/aspectRatio).
        fov: node.isPerspectiveCamera ? node.fov : cfg.fallbackFov,
        aspect: node.isPerspectiveCamera && node.aspect > 0 ? node.aspect : 0,
        // Resolved once: CameraManager hands these objects straight out, so the
        // per-frame visibility gates allocate nothing.
        dressing: {
          interior,
          // `_pilot` only has anything to say about an interior shot, where the
          // default — the cockpit view's — is to hide him. A mount out on a boom
          // is dressed like the chase view, shell and pilot both up: an empty
          // canopy on a fly-by would be a worse default than any tag can fix.
          pilot: !interior || name.includes(cfg.tagPilot),
        },
      };
    });

    this.index = 0;
  }

  get count() {
    return this.shots.length;
  }

  /** The mount currently framed, or null if the model carries none. */
  get shot() {
    return this.shots[this.index] || null;
  }

  /** Jump to the first shot (entering the mode). */
  reset() {
    this.index = 0;
  }

  /** Advance to the next shot, wrapping — the mode is left via Enter. */
  advance() {
    if (this.count === 0) return;
    this.index = (this.index + 1) % this.count;
  }

  /**
   * Vertical FOV for the active shot at the live viewport aspect.
   *
   * A Blender camera's focal length sets its *horizontal* field of view (sensor
   * fit Auto on a landscape render); glTF stores the vertical angle that worked
   * out to at the authored aspect. Re-deriving the vertical angle for the actual
   * window is what keeps the lens honest on an aspect the shot wasn't framed
   * for: at the authored aspect it's an identity, and on anything taller the
   * extra height comes out of the top and bottom instead of the focal length
   * quietly getting longer. `fovFit: 'vertical'` takes the exported angle as-is.
   */
  fovFor(aspect) {
    const shot = this.shot;
    if (!shot) return CAMERA.action.fallbackFov;
    if (CAMERA.action.fovFit !== 'horizontal' || !shot.aspect || !(aspect > 0)) return shot.fov;
    const hTan = Math.tan(shot.fov * 0.5 * DEG2RAD) * shot.aspect;
    return clamp(2 * Math.atan(hTan / aspect) * RAD2DEG, 1, 170);
  }

  update() {
    const shot = this.shot;
    if (!shot) return;
    // getWorld* refresh the ancestor chain themselves, so this is correct
    // wherever in the frame the manager ticks.
    shot.node.getWorldPosition(this.position);
    shot.node.getWorldQuaternion(this.quaternion);
  }
}
